import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Inventory as InventoryIcon,
  Business,
  AttachMoney,
  CalendarToday,
  LocationOn,
  Warning,
  AddCircle,
} from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { exportToExcel, exportToPDF } from '../../utils/exportHelpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { extractAndTranslateError, formatNumber, formatCurrency, formatDate } from '../../utils/i18nHelpers';

interface InventoryItem {
  id: number;
  item_name: string;
  description?: string;
  category: string;
  unit?: string;
  current_stock: number;
  cost_price?: number;
  supplier?: string;
  supplier_contact?: string;
  last_restocked?: string;
  location?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const Inventory: React.FC = () => {
  const { can } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { t, language } = useLanguage();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittingStock, setSubmittingStock] = useState(false);
  const [submittingCategory, setSubmittingCategory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    item_name: '',
    description: '',
    category: '',
    unit: '',
    cost_price: '',
    supplier: '',
    supplier_contact: '',
    last_restocked: '',
    location: '',
    is_active: true,
  });
  const [stockUpdateOpen, setStockUpdateOpen] = useState(false);
  const [stockUpdateItem, setStockUpdateItem] = useState<InventoryItem | null>(null);
  const [stockUpdateData, setStockUpdateData] = useState({
    quantity_added: '',
    notes: '',
  });
  const [categories, setCategories] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoriesListOpen, setCategoriesListOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<{ id?: number; name: string; description?: string } | null>(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchInventory();
    fetchCategories();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await api.get('/inventory');
      setInventory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setInventory([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/inventory-categories');
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const handleOpen = (item?: InventoryItem) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        item_name: item.item_name,
        description: item.description || '',
        category: item.category,
        unit: item.unit || '',
        cost_price: item.cost_price?.toString() || '',
        supplier: item.supplier || '',
        supplier_contact: item.supplier_contact || '',
        last_restocked: item.last_restocked ? new Date(item.last_restocked).toISOString().split('T')[0] : '',
        location: item.location || '',
        is_active: item.is_active,
      });
    } else {
      setEditingItem(null);
      setFormData({
        item_name: '',
        description: '',
        category: '',
        unit: '',
        cost_price: '',
        supplier: '',
        supplier_contact: '',
        last_restocked: '',
        location: '',
        is_active: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingItem(null);
  };

  const handleOpenStockUpdate = (item: InventoryItem) => {
    setStockUpdateItem(item);
    setStockUpdateData({
      quantity_added: '',
      notes: '',
    });
    setStockUpdateOpen(true);
  };

  const handleCloseStockUpdate = () => {
    setStockUpdateOpen(false);
    setStockUpdateItem(null);
    setStockUpdateData({
      quantity_added: '',
      notes: '',
    });
  };

  const handleStockUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stockUpdateItem || submittingStock) return; // Prevent double submission
    setSubmittingStock(true);
    try {
      const quantityToAdd = Math.max(0, Number(stockUpdateData.quantity_added) || 0);
      if (!quantityToAdd) {
        notifyError(t('inventory.messages.stock.positiveRequired'));
        return;
      }

      await api.patch(`/inventory/${stockUpdateItem.id}/stock`, {
        quantity_added: quantityToAdd,
        notes: stockUpdateData.notes || null,
      });
      fetchInventory();
      handleCloseStockUpdate();
      notifySuccess(t('inventory.messages.stock.updateSuccess'));
    } catch (error: any) {
      console.error('Error updating stock:', error);
      const msg =
        error.response?.data?.message || t('inventory.messages.stock.updateError');
      notifyError(msg);
    } finally {
      setSubmittingStock(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
        last_restocked: formData.last_restocked || null,
      };

      if (editingItem) {
        await api.put(`/inventory/${editingItem.id}`, submitData);
        notifySuccess(t('inventory.messages.item.saveUpdateSuccess'));
      } else {
        // For new items, current_stock is required - set to 0, user can update stock separately
        await api.post('/inventory', {
          ...submitData,
          current_stock: 0,
        });
        notifySuccess(t('inventory.messages.item.saveCreateSuccess'));
      }
      fetchInventory();
      handleClose();
    } catch (error) {
      console.error('Error saving inventory item:', error);
      const msg =
        (error as any)?.response?.data?.error ||
        t('inventory.messages.item.saveError');
      notifyError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('inventory.messages.item.deleteConfirm'))) {
      try {
        await api.delete(`/inventory/${id}`);
        fetchInventory();
        notifySuccess(t('inventory.messages.item.deleteSuccess'));
      } catch (error) {
        console.error('Error deleting inventory item:', error);
        const msg =
          (error as any)?.response?.data?.error ||
          t('inventory.messages.item.deleteError');
        notifyError(msg);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const numericFields = ['cost_price'];

    if (type === 'number' && numericFields.includes(name)) {
      const sanitized = value === '' ? '' : Math.max(0, Number(value));
      setFormData(prev => ({ ...prev, [name]: sanitized }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryOpen = (category?: { id: number; name: string; description?: string }) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({ name: category.name, description: category.description || '' });
    } else {
      setEditingCategory(null);
      setCategoryFormData({ name: '', description: '' });
    }
    setCategoryDialogOpen(true);
  };

  const handleCategoryClose = () => {
    setCategoryDialogOpen(false);
    setEditingCategory(null);
    setCategoryFormData({ name: '', description: '' });
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingCategory) return; // Prevent double submission
    setSubmittingCategory(true);
    try {
      if (editingCategory?.id) {
        await api.put(`/inventory-categories/${editingCategory.id}`, categoryFormData);
        notifySuccess(t('inventory.categories.messages.updateSuccess'));
      } else {
        await api.post('/inventory-categories', categoryFormData);
        notifySuccess(t('inventory.categories.messages.createSuccess'));
        // If adding from inventory form, set the new category as selected
        if (!categoriesListOpen) {
          setFormData({ ...formData, category: categoryFormData.name });
        }
      }
      fetchCategories();
      handleCategoryClose();
    } catch (error: any) {
      console.error('Error saving category:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.errors?.name?.[0] ||
        t('inventory.categories.messages.saveError');
      notifyError(message);
    } finally {
      setSubmittingCategory(false);
    }
  };

  const handleCategoryDelete = async (id: number) => {
    if (!window.confirm(t('inventory.categories.messages.deleteConfirm'))) return;
    try {
      await api.delete(`/inventory-categories/${id}`);
      notifySuccess(t('inventory.categories.messages.deleteSuccess'));
      fetchCategories();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      const message =
        error?.response?.data?.error ||
        t('inventory.categories.messages.deleteError');
      notifyError(message);
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'new': return 'success';
      case 'good': return 'info';
      case 'fair': return 'warning';
      case 'poor': return 'error';
      case 'damaged': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'used': return 'info';
      case 'disposed': return 'error';
      case 'missing': return 'error';
      default: return 'default';
    }
  };

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 3600 * 24));
    return diffDays <= 30 && diffDays >= 0;
  };

  const isExpired = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    return expiry < now;
  };

  if (loading) {
    return <div>{t('inventory.loading')}</div>;
  }

  const filteredInventory = inventory.filter((item) => {
    const term = searchTerm.toLowerCase();
    return (
      item.item_name.toLowerCase().includes(term) ||
      (item.category || '').toLowerCase().includes(term) ||
      (item.location || '').toLowerCase().includes(term)
    );
  });

  const handleExportExcel = async () => {
    if (!filteredInventory.length) {
      notifyError(t('inventory.export.noData'));
      return;
    }
    const rows = filteredInventory.map((item) => ({
      [t('inventory.export.columns.name')]: item.item_name,
      [t('inventory.export.columns.category')]: item.category,
      [t('inventory.export.columns.stock')]: item.current_stock,
      [t('inventory.export.columns.costPrice')]: item.cost_price ?? '',
      [t('inventory.export.columns.unit')]: item.unit || '',
      [t('inventory.export.columns.supplier')]: item.supplier || '',
      [t('inventory.export.columns.location')]: item.location || '',
      [t('inventory.export.columns.active')]:
        item.is_active ? t('inventory.common.yes') : t('inventory.common.no'),
    }));
    await exportToExcel(rows, 'inventory');
    notifySuccess(t('inventory.export.excel.success'));
  };

  const handleExportPDF = async () => {
    if (!filteredInventory.length) {
      notifyError(t('inventory.export.noData'));
      return;
    }
    const columns = [
      t('inventory.dialog.item.fields.itemName'),
      t('inventory.dialog.item.fields.category'),
      t('inventory.export.columns.stock'),
      t('inventory.dialog.item.fields.costPrice'),
      t('inventory.dialog.item.fields.unit'),
      t('inventory.dialog.item.fields.supplier'),
      t('inventory.dialog.item.fields.location'),
      t('inventory.card.status.active'),
    ];
    const data = filteredInventory.map((item) => [
      item.item_name,
      item.category,
      item.current_stock,
      item.cost_price ?? '',
      item.unit || '',
      item.supplier || '',
      item.location || '',
      item.is_active ? t('inventory.common.yes') : t('inventory.common.no'),
    ]);
    await exportToPDF(columns, data, 'inventory', language, t('inventory.title'));
    notifySuccess(t('inventory.export.pdf.success'));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">{t('inventory.title')}</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder={t('inventory.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {can('inventory.export-excel') && (
            <Button variant="outlined" onClick={handleExportExcel}>
              {t('inventory.export.excel')}
            </Button>
          )}
          {can('inventory.export-pdf') && (
            <Button variant="outlined" onClick={handleExportPDF}>
              {t('inventory.export.pdf')}
            </Button>
          )}
          {can('inventory.add') && (
            <Button
              variant="outlined"
              onClick={() => setCategoriesListOpen(true)}
            >
              {t('inventory.toolbar.manageCategories')}
            </Button>
          )}
          {can('inventory.add') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpen()}
            >
              {t('inventory.toolbar.addItem')}
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>
        {filteredInventory.map((item) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Box>
                    <Typography variant="h6">{item.item_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.category}
                    </Typography>
                  </Box>
                  <Box>
                    {can('inventory.edit') && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenStockUpdate(item)}
                          color="primary"
                          title={t('inventory.dialog.stock.buttonTitle')}
                        >
                          <AddCircle />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpen(item)}
                        >
                          <Edit />
                        </IconButton>
                      </>
                    )}
                    {can('inventory.delete') && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" mb={1}>
                  <InventoryIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {formatNumber(item.current_stock, language)}{' '}
                    {item.unit || t('inventory.card.stock.unitsFallback')}
                  </Typography>
                </Box>
                
                {item.cost_price && (
                  <Box display="flex" alignItems="center" mb={1} gap={2}>
                    <Typography variant="body2">
                      <strong>{t('inventory.card.cost.label')}</strong>{' '}
                      {formatCurrency(item.cost_price, language)}
                    </Typography>
                    {item.unit && (
                      <Typography variant="body2" color="text.secondary">
                        {t('inventory.card.cost.perUnit').replace(
                          '{{unit}}',
                          item.unit
                        )}
                      </Typography>
                    )}
                  </Box>
                )}
                
                <Box display="flex" gap={1} mb={2}>
                  <Chip
                    label={
                      item.is_active
                        ? t('inventory.card.status.active')
                        : t('inventory.card.status.inactive')
                    }
                    size="small"
                    color={item.is_active ? 'success' : 'error'}
                  />
                </Box>
                
                {item.last_restocked && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarToday fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {t('inventory.card.lastRestocked')}{' '}
                      {formatDate(item.last_restocked, language)}
                    </Typography>
                  </Box>
                )}
                
                {item.location && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <LocationOn fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {item.location}
                    </Typography>
                  </Box>
                )}
                
                {item.supplier && (
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>{t('inventory.card.supplier')}</strong> {item.supplier}
                  </Typography>
                )}
                
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem
            ? t('inventory.dialog.item.editTitle')
            : t('inventory.dialog.item.addTitle')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('inventory.dialog.item.fields.itemName')}
                  name="item_name"
                  value={formData.item_name}
                  onChange={handleInputChange}
                  required
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>
                    {t('inventory.dialog.item.fields.category')}
                  </InputLabel>
                  <Select
                    value={formData.category}
                    label={t('inventory.dialog.item.fields.category')}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {can('inventory.add') && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryFormData({ name: '', description: '' });
                      setCategoryDialogOpen(true);
                    }}
                    sx={{ minWidth: 120 }}
                  >
                    <AddCircle sx={{ mr: 1 }} />
                    {t('inventory.dialog.category.addButton')}
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('inventory.dialog.item.fields.location')}
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('inventory.dialog.item.fields.unit')}
                  name="unit"
                  value={formData.unit}
                  onChange={handleInputChange}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('inventory.dialog.item.fields.costPrice')}
                  name="cost_price"
                  type="number"
                  value={formData.cost_price}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Box>
              
              <TextField
                fullWidth
                label={t('inventory.dialog.item.fields.description')}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('inventory.dialog.item.fields.supplier')}
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleInputChange}
                />
                <TextField
                  fullWidth
                  label={t('inventory.dialog.item.fields.supplierContact')}
                  name="supplier_contact"
                  value={formData.supplier_contact}
                  onChange={handleInputChange}
                />
              </Box>
              
              <TextField
                fullWidth
                label={t('inventory.dialog.item.fields.lastRestocked')}
                name="last_restocked"
                type="date"
                value={formData.last_restocked}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    name="is_active"
                    color="primary"
                  />
                }
                label={t('inventory.dialog.item.fields.isActive')}
              />
              
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>
              {t('inventory.dialog.item.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={20} /> : null}>
              {editingItem
                ? t('inventory.dialog.item.update')
                : t('inventory.dialog.item.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Stock Update Dialog */}
      <Dialog open={stockUpdateOpen} onClose={handleCloseStockUpdate} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('inventory.dialog.stock.title').replace(
            '{{itemName}}',
            stockUpdateItem?.item_name || ''
          )}
        </DialogTitle>
        <form onSubmit={handleStockUpdate}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {t('inventory.dialog.stock.current')}{' '}
                  <strong>
                    {t('inventory.dialog.stock.current.unitsSuffix')
                      .replace(
                        '{{quantity}}',
                        String(stockUpdateItem?.current_stock || 0)
                      )
                      .replace(
                        '{{unit}}',
                        stockUpdateItem?.unit ||
                          t('inventory.card.stock.unitsFallback')
                      )}
                  </strong>
                </Typography>
              </Box>
              
              <TextField
                fullWidth
                label={t('inventory.dialog.stock.quantityToAdd')}
                name="quantity_added"
                type="number"
                value={stockUpdateData.quantity_added}
                onChange={(e) => {
                  const next = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                  setStockUpdateData({ ...stockUpdateData, quantity_added: next === '' ? '' : next.toString() });
                }}
                required
                helperText={t('inventory.dialog.stock.quantityToAdd.helper')}
                inputProps={{ step: "1", min: 0 }}
              />
              
              {stockUpdateData.quantity_added && !isNaN(Number(stockUpdateData.quantity_added)) && (
                <Typography variant="body2" color="text.secondary">
                  {t('inventory.dialog.stock.newStock')}{' '}
                  <strong>
                    {t('inventory.dialog.stock.newStock.unitsSuffix')
                      .replace(
                        '{{quantity}}',
                        String(
                          Number(stockUpdateData.quantity_added) +
                            (stockUpdateItem?.current_stock || 0)
                        )
                      )
                      .replace(
                        '{{unit}}',
                        stockUpdateItem?.unit ||
                          t('inventory.card.stock.unitsFallback')
                      )}
                  </strong>
                </Typography>
              )}
              
              <TextField
                fullWidth
                label={t('inventory.dialog.stock.notes')}
                name="notes"
                value={stockUpdateData.notes}
                onChange={(e) => setStockUpdateData({ ...stockUpdateData, notes: e.target.value })}
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseStockUpdate}>
              {t('inventory.dialog.stock.cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={!stockUpdateData.quantity_added || isNaN(Number(stockUpdateData.quantity_added)) || Number(stockUpdateData.quantity_added) <= 0}
            >
              {t('inventory.dialog.stock.submit')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={categoryDialogOpen} onClose={handleCategoryClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory
            ? t('inventory.dialog.category.editTitle')
            : t('inventory.dialog.category.addTitle')}
        </DialogTitle>
        <form onSubmit={handleCategorySubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label={t('inventory.dialog.category.fields.name')}
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                required
                autoFocus
              />
              <TextField
                fullWidth
                label={t('inventory.dialog.category.fields.description')}
                value={categoryFormData.description}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCategoryClose} disabled={submittingCategory}>
              {t('inventory.dialog.category.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={submittingCategory || !categoryFormData.name.trim()} startIcon={submittingCategory ? <CircularProgress size={20} /> : null}>
              {editingCategory
                ? t('inventory.dialog.category.update')
                : t('inventory.dialog.category.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Categories List Dialog */}
      <Dialog open={categoriesListOpen} onClose={() => setCategoriesListOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {t('inventory.dialog.categories.manageTitle')}
            </Typography>
            {can('inventory.add') && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryFormData({ name: '', description: '' });
                  setCategoryDialogOpen(true);
                }}
              >
                {t('inventory.dialog.category.addButton')}
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {categories.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {t('inventory.dialog.categories.empty')}
              </Typography>
            ) : (
              categories.map((category) => (
                <Card key={category.id} variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6">{category.name}</Typography>
                        {category.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {category.description}
                          </Typography>
                        )}
                      </Box>
                      {can('inventory.edit') && (
                        <Box display="flex" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleCategoryOpen(category)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                          {can('inventory.delete') && (
                            <IconButton
                              size="small"
                              onClick={() => handleCategoryDelete(category.id)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoriesListOpen(false)}>
            {t('inventory.dialog.categories.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;